import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, FileText, ChevronLeft, ChevronRight, ChevronDown, UserPlus, User, Building2, X } from 'lucide-react';
import { api } from '../services/api';
import { exportAndDownload } from '../utils/downloadPdf';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { useIsMobile } from '../hooks/useIsMobile';
import AddContactModal from '../components/AddContactModal';
import Avatar from '../components/Avatar';
import AgencyLogo from '../components/AgencyLogo';
import type { Contact, Agency, Role } from '../types';

const PAGE_SIZE = 50;

// Labels reflect the live, admin-configurable thresholds (E77).
const verificationLabels = (agingDays: number, overdueDays: number): Record<string, string> => ({
  verified: `Verified (≤${agingDays}d)`,
  aging:    `Aging (${agingDays}–${overdueDays}d)`,
  overdue:  `Overdue (${overdueDays}d+)`,
  never:    'Never Verified',
});

const DirectoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { agingDays, overdueDays } = useSettings();
  const VERIFICATION_LABELS = verificationLabels(agingDays, overdueDays);

  const initialVerification = searchParams.get('verification') ?? '';

  const [q, setQ] = useState('');
  const [agencyId, setAgencyId] = useState('');
  const [roleId, setRoleId] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState({ q: '', agencyId: '', roleId: '', activeOnly: true, verification: initialVerification });

  // When URL param changes (e.g. navigating from KPI tile), apply it
  useEffect(() => {
    const v = searchParams.get('verification') ?? '';
    setOffset(0);
    setSearch(prev => ({ ...prev, verification: v }));
  }, [searchParams]);

  const { data: contactsData, isLoading } = useQuery({
    queryKey: ['contacts', search, offset],
    queryFn: () =>
      api.searchContacts({
        q: search.q || undefined,
        agency_id: search.agencyId || undefined,
        role_id: search.roleId || undefined,
        active_only: String(search.activeOnly),
        verification: search.verification || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
    placeholderData: (prev) => prev,
  });

  const { data: agencies } = useQuery<Agency[]>({
    queryKey: ['agencies-all'],
    queryFn: () => api.getAgencies({ limit: 500 }).then((r) => r.data ?? []),
    staleTime: 5 * 60 * 1000,
  });

  const { data: roles } = useQuery<Role[]>({
    queryKey: ['roles'],
    queryFn: () => api.getRoles(),
    staleTime: 5 * 60 * 1000,
  });

  const contacts: Contact[] = contactsData?.data ?? [];
  const total: number = contactsData?.meta?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setOffset(0);
      setSearch(prev => ({ q, agencyId, roleId, activeOnly, verification: prev.verification }));
    },
    [q, agencyId, roleId, activeOnly]
  );

  const clearVerification = useCallback(() => {
    setSearchParams({});
    setOffset(0);
    setSearch(prev => ({ ...prev, verification: '' }));
  }, [setSearchParams]);

  const isMobile = useIsMobile();
  const [openPopupId, setOpenPopupId] = useState<string | null>(null);
  const [showAddContact, setShowAddContact] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setExportOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const handler = () => setOpenPopupId(null);
    if (openPopupId) {
      document.addEventListener('click', handler);
      return () => document.removeEventListener('click', handler);
    }
  }, [openPopupId]);

  const isFiltered = !!(search.q || search.agencyId || search.roleId || search.verification || !search.activeOnly);

  const exportPDF = async () => {
    setExportOpen(false);
    if (exporting) return;
    setExporting(true);
    try {
      const filterParams = {
        ...(search.q          && { q:            search.q }),
        ...(search.agencyId   && { agency_id:    search.agencyId }),
        ...(search.roleId     && { role_id:      search.roleId }),
        ...(search.verification && { verification: search.verification }),
        ...(!search.activeOnly  && { active_only:  'false' }),
      };
      await exportAndDownload(() => api.exportDirectoryPDF(filterParams), 'iad-directory.pdf');
    } catch (err: any) {
      alert(err?.message ?? 'Export failed.');
    } finally {
      setExporting(false);
    }
  };

  const exportCSV = async () => {
    setExportOpen(false);
    if (exporting) return;
    setExporting(true);
    try {
      const fetchLimit = Math.min(total || PAGE_SIZE, 1000);
      const result = await api.searchContacts({
        q:            search.q       || undefined,
        agency_id:    search.agencyId || undefined,
        role_id:      search.roleId   || undefined,
        active_only:  String(search.activeOnly),
        verification: search.verification || undefined,
        limit:        fetchLimit,
        offset:       0,
      });
      const allContacts = result.data ?? [];
      const headers = ['Name', 'Title', 'Agency', 'Phone', 'Email', 'Type'];
      const rows = allContacts.map((c: Contact) => {
        const name  = c.full_name || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim();
        const phone = c.phones?.find((p) => p.is_preferred)?.phone_number ?? c.preferred_phones?.[0] ?? '';
        return [
          name,
          c.title ?? '',
          c.agency_acronym ?? c.agency_name ?? '',
          phone,
          c.email_primary ?? '',
          c.classification_id === 2 ? 'Restricted' : 'Standard',
        ];
      });
      const csv = [headers, ...rows]
        .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
        .join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = isFiltered ? 'iad-directory-filtered.csv' : 'iad-directory.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err?.message ?? 'CSV export failed.');
    } finally {
      setExporting(false);
    }
  };

  const canExport = user?.role === 'Administrator' || user?.role === 'Superuser';
  const canEdit = user?.role === 'Administrator' || user?.role === 'Superuser';

  return (
    <div style={{ padding: '0 0 32px' }}>
      {/* Search bar */}
      <form onSubmit={handleSearch} style={{ marginBottom: 16 }}>
        <div className="searchbar">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search contacts by name, email, title…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button type="submit" className="btn btn-primary" style={{ marginLeft: 8 }}>
            Search
          </button>
          {canExport && (
            <div ref={exportRef} style={{ position: 'relative', marginLeft: 8 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setExportOpen((o) => !o)}
                disabled={exporting}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <FileText size={14} />
                {exporting ? 'Exporting…' : `Export${total > 0 ? ` (${total.toLocaleString()})` : ''}`}
                <ChevronDown size={12} />
              </button>
              {exportOpen && (
                <div style={{
                  position: 'absolute', right: 0, top: '100%', marginTop: 4,
                  background: 'white', border: '1px solid var(--gray-200)',
                  borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  zIndex: 100, minWidth: 220,
                }}>
                  <div style={{ padding: '6px 14px 4px', fontSize: 12, color: 'var(--gray-500)', fontWeight: 600, textTransform: 'uppercase' }}>
                    {isFiltered ? 'Filtered results' : 'Full directory'} · {total.toLocaleString()} contacts
                  </div>
                  <button
                    type="button"
                    onClick={exportPDF}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, textAlign: 'left' }}
                  >
                    <FileText size={14} /> {isFiltered ? 'Export Filtered PDF' : 'Export Full Directory PDF'}
                  </button>
                  <button
                    type="button"
                    onClick={exportCSV}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, textAlign: 'left', borderTop: '1px solid var(--gray-100)' }}
                  >
                    <FileText size={14} /> {isFiltered ? 'Export Filtered CSV' : 'Export Full Directory CSV'}
                  </button>
                </div>
              )}
            </div>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={() => setShowAddContact(true)}
              style={{
                background: 'var(--navy)',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                padding: '6px 14px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginLeft: 8,
              }}
            >
              <UserPlus size={14} />
              Add Contact
            </button>
          )}
        </div>
      </form>

      {showAddContact && (
        <AddContactModal onClose={() => setShowAddContact(false)} />
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          aria-label="Filter by agency"
          value={agencyId}
          onChange={(e) => setAgencyId(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--gray-300)', fontSize: 14 }}
        >
          <option value="">All Agencies</option>
          {agencies?.map((a) => (
            <option key={a.id} value={a.id}>
              {a.acronym} — {a.name}
            </option>
          ))}
        </select>

        <select
          aria-label="Filter by role"
          value={roleId}
          onChange={(e) => setRoleId(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--gray-300)', fontSize: 14 }}
        >
          <option value="">All Roles</option>
          {roles?.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => {
              const checked = e.target.checked;
              setActiveOnly(checked);
              setOffset(0);
              setSearch((prev) => ({ ...prev, activeOnly: checked }));
            }}
          />
          Active only
        </label>

        {total > 0 && (
          <span style={{ fontSize: 14, color: 'var(--gray-500)', marginLeft: 'auto' }}>
            {total.toLocaleString()} result{total !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Active verification filter chip */}
      {search.verification && VERIFICATION_LABELS[search.verification] && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>Filtered by:</span>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'var(--sky-50)', color: 'var(--electric)', border: '1px solid var(--sky)',
            borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600,
          }}>
            {VERIFICATION_LABELS[search.verification]}
            <button
              onClick={clearVerification}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: 'var(--electric)' }}
              aria-label="Remove filter"
            >
              <X size={12} />
            </button>
          </span>
        </div>
      )}

      {/* Table */}
      <div className="card" style={{ padding: 0 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Name</th>
              <th>Title</th>
              <th>Agency</th>
              {!isMobile && <th>Email</th>}
              {!isMobile && <th>Phone</th>}
              <th>Type</th>
              {!isMobile && <th>Verified</th>}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={isMobile ? 5 : 8} style={{ textAlign: 'center', padding: 32, color: 'var(--gray-500)' }}>
                  Loading…
                </td>
              </tr>
            ) : contacts.length === 0 ? (
              <tr>
                <td colSpan={isMobile ? 5 : 8} style={{ textAlign: 'center', padding: 32, color: 'var(--gray-500)' }}>
                  No contacts found
                </td>
              </tr>
            ) : (
              contacts.map((c) => {
                const preferredPhone = (() => {
                  const p = c.phones?.find((ph) => ph.is_preferred);
                  if (p) return `${p.phone_number}${p.extension ? ` x${p.extension}` : ''}`;
                  return c.preferred_phones?.[0] ?? null;
                })();
                const displayName = c.full_name || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim();
                const isArchived = !c.is_active;
                return (
                  <tr
                    key={c.id}
                    style={{ cursor: 'pointer', opacity: isArchived ? 0.65 : 1 }}
                    onClick={() => navigate(`/contacts/${c.id}`)}
                  >
                    <td style={{ fontWeight: 500 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar name={displayName} size={26} />
                        <span>
                          {displayName}
                          {isArchived && (
                            <span style={{ marginLeft: 6, fontSize: 12, background: 'var(--amber-50)', color: 'var(--amber-text)', borderRadius: 10, padding: '1px 6px', fontWeight: 600 }}>
                              Archived
                            </span>
                          )}
                        </span>
                      </div>
                    </td>
                    <td style={{ color: 'var(--gray-600)' }}>{c.title ?? '—'}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {(c.agency_name || c.agency_acronym) && (
                          <AgencyLogo
                            name={c.agency_name ?? c.agency_acronym ?? ''}
                            acronym={c.agency_acronym ?? undefined}
                            size={20}
                          />
                        )}
                        {!isMobile && <span className="chip">{c.agency_acronym ?? c.agency_name ?? '—'}</span>}
                      </div>
                    </td>
                    {!isMobile && <td style={{ fontSize: 14 }}>{c.email_primary ?? '—'}</td>}
                    {!isMobile && (
                      <td style={{ fontSize: 14 }}>
                        {preferredPhone ?? '—'}
                      </td>
                    )}
                    <td>
                      <span className={`pill ${c.classification_id === 2 ? 'pill-restricted' : 'pill-standard'}`}>
                        {c.classification_id === 2 ? 'Restricted' : 'Standard'}
                      </span>
                    </td>
                    {!isMobile && (
                      <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                        {c.last_verified_at
                          ? new Date(c.last_verified_at).toLocaleDateString()
                          : 'Never'}
                      </td>
                    )}
                    <td style={{ position: 'relative', whiteSpace: 'nowrap' }}>
                      <button
                        className="btn btn-secondary"
                        style={{ fontSize: 12, padding: '3px 10px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenPopupId(openPopupId === c.id ? null : c.id);
                        }}
                      >
                        Request Update
                      </button>
                      {openPopupId === c.id && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            zIndex: 50,
                            background: 'var(--white)',
                            border: '1px solid var(--gray-200)',
                            borderRadius: 8,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                            minWidth: 220,
                            overflow: 'hidden',
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            className="popup-menu-btn"
                            onClick={() => {
                              setOpenPopupId(null);
                              navigate(`/update-requests?create=1&agency_id=${c.agency_id}&contact_id=${c.id}`);
                            }}
                          >
                            <User size={13} />
                            This contact only
                          </button>
                          <button
                            className="popup-menu-btn"
                            onClick={() => {
                              setOpenPopupId(null);
                              navigate(`/update-requests?create=1&agency_id=${c.agency_id}`);
                            }}
                          >
                            <Building2 size={13} />
                            All contacts at {c.agency_name || c.agency_acronym}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            marginTop: 16,
          }}
        >
          <button
            className="btn btn-secondary"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            <ChevronLeft size={14} />
            Prev
          </button>
          <span style={{ fontSize: 14 }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="btn btn-secondary"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
            <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default DirectoryPage;
